<template>
  <div class="card">
    <div class="icon-holder">
      <i class="icon-study"></i>
    </div>
    <div class="info-container">
      <div class="name">
        <input
          v-show="nameEdit"
          ref="name"
          v-model="name"
          type="text"
          :class="{ error: nameError }"
          @blur="cancelEditName()"
          @keypress.esc="cancelEditName()"
          @keypress.enter="editName()"
        />
        <span v-if="!nameEdit" class="name-label">{{ name }}</span>
        <i v-if="!nameEdit && userInfo.role_id !== 3" class="icon-edit icon-edit-name name-edit" :title="$t('editTitle')" @click="openEditName()"></i>
        <i v-else-if="userInfo.role_id !== 3" class="icon-checkstatus name-edit" :title="$t('saveNewTitle')" @click.stop="editName()"></i>
      </div>
      <div class="client">{{ $t('client') }} : {{ data.clientName }}</div>
      <div class="meta-container">
        <div class="date">{{ $t('createdOn') }} : {{ formatDate(data.created_at) }}</div>
        <div class="author">
          {{ $t('by') }} {{ data.user ? data.user.firstname : '' }} {{ data.user ? data.user.lastname : '' }} <br /><span
            v-if="data.countries && data.countries.length && data.countries[0].length > 0"
            class="country"
            >(<span v-for="(country, index) in data.countries" :key="country" :style="{ marginRight: index + 1 === data.countries.length ? 0 : '4px' }">{{
              index + 1 === data.countries.length ? country : `${country}, `
            }}</span
            >)</span
          >
        </div>
        <div class="status" :class="{ valid: [8, 12, 13].includes(code) || data.needRefresh }" :title="$t(`studyCode.${code}`)">
          <span class="text">
            {{ $t(`studyCode.${code}`) }}
          </span>
        </div>
      </div>
    </div>
    <div class="action-container">
      <div class="icon-container">
        <div class="icon">
          <i class="icon-download" :title="$t('exportCsv')" @click="() => exportCsv(data)"></i>
        </div>
        <div class="icon">
          <i class="icon-addperson" :title="$t('share')" @click="() => shareStudy(false)"></i>
          <div v-if="userInfo.role_id !== 3" class="select-box">
            <div class="select-entry" @click="() => shareStudy(true)">{{ $t('modal.internalShareShort') }}</div>
            <div class="select-entry" @click="() => shareStudy(false)">{{ $t('modal.clientShareShort') }}</div>
          </div>
        </div>

        <div v-if="([12, 13].includes(code) || data.needRefresh) && userInfo.role_id !== 3" class="icon">
          <i
            class="icon-edit"
            :class="{ active: data.needRefresh }"
            :title="data.needRefresh ? $t('continueEditStudy') : $t('editStudy')"
            @click="editStudy()"
          ></i>
        </div>
        <div class="icon">
          <i class="icon-duplicate" :title="$t('duplicate')" @click="duplicateStudy"></i>
        </div>

        <div v-if="[9, 10, 12, 13, 17].includes(code) || data.needRefresh" class="icon">
          <i class="icon-cogfilled" :title="$t('settings')" @click="studyParameters"></i>
        </div>
        <div v-if="[12, 13].includes(code) || data.needRefresh" class="icon">
          <i class="icon-exportthin" :title="$t('toExport')" @click="exportStudy"></i>
        </div>
        <div class="icon">
          <i class="icon-basket" :title="$t('delete')" @click="deleteStudy"></i>
        </div>
      </div>
      <button v-if="code === null" class="study-button" @click="editStudy()">{{ $t('loading') }}</button>
      <button v-if="(code !== null && ![8, 12, 13, 14, 17].includes(code)) || data.needRefresh" class="study-button" @click="editStudy()">
        {{ $t('resume') }}
      </button>
      <button v-if="code !== null && [12, 13, 17].includes(code) && !data.needRefresh" class="study-button" @click="seeResult()">
        {{ $t('seeTheStudy') }}
      </button>
      <button v-if="!fetching && [8, 14].includes(code) && !data.needRefresh" class="study-button study-error">
        {{ $t('Error') }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { formatDate } from '@/libraries/formatter';

export default {
  props: ['data'],

  data() {
    return {
      formatDate,
      fetching: false,
      status: '...',
      code: null,
      nameEdit: false,
      nameError: false,
      name: '',
      prevName: '',
      edited: false,
      draft_id: undefined,
    };
  },

  computed: {
    ...mapGetters(['isDesktop', 'userInfo']),
  },

  mounted() {
    this._isUnmounted = false;
    this.fetching = true;
    this.fetchProcess();
    this.name = this.data.name;
    this.prevName = this.data.name;
  },

  beforeUnmount() {
    this._isUnmounted = true;

    clearTimeout(this.fetchTimeoutId);
  },

  methods: {
    fetchProcess() {
      if (this._isUnmounted) return;

      const id = this.data.id;

      this.$store.dispatch('fetchProcessStatus', { id }).then(response => {
        if (this._isUnmounted) return;

        const { code, value } = response;
        this.status = value;
        this.code = code;
        if (code >= 4 && code <= 7) {
          this.fetchTimeoutId = setTimeout(() => this.fetchProcess(), 2000);
        } else {
          this.fetching = false;
        }
      });
    },

    shareStudy(isInternal) {
      const id = this.data.id;
      this.$store.dispatch('updateCurrentModal', { component: 'ShareStudy', params: { id, isInternal } });
    },

    async exportCsv(study) {
      const csvData = await this.$store.dispatch('exportStudyCsv', {
        studyId: study.id,
      });
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8,' });
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.setAttribute('href', blobUrl);
      link.setAttribute('download', `${study.name}.csv`);
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    },

    studyParameters() {
      const id = this.data.id;
      this.$store.dispatch('updateCurrentModal', { component: 'StudyParameters', params: { id } });
    },

    deleteStudy() {
      const id = this.data.id;
      const isOwner = this.data.user.email === this.userInfo.email;
      this.$store.dispatch('updateCurrentModal', { component: 'DeleteStudy', params: { id, isOwner } });
    },

    exportStudy() {
      const id = this.data.id;
      this.$store.dispatch('updateCurrentModal', { component: 'ExportStudyList', params: { id } });
    },

    async getDraftId() {
      this.draftId = this.data.draft_study_id ? this.data.draft_study_id : await this.$store.dispatch('getDraftId', { originalStudyId: this.data.id });
    },

    async getRedirectData() {
      // if study has alreary been completed, get draft data
      if (this.code > 11) {
        if (!this.draftId) {
          await this.getDraftId();
        }
        const draftStudy = await this.$store.dispatch('fetchProcessStatus', {
          id: this.draftId,
        });

        return {
          studyId: this.draftId,
          code: draftStudy.code,
        };
      }

      return {
        studyId: this.data.id,
        code: this.code,
      };
    },

    async editStudy(data = null) {
      const { studyId, code } = data && data.studyId ? data : await this.getRedirectData();

      if (code === 1) {
        this.$router.push({ name: 'studyUploadCSV', params: { studyId } });
      } else if (code === 2) {
        this.$router.push({ name: 'studyUpdateFilters', params: { studyId } });
      } else if (code === 3 || code === 4) {
        this.$router.push({ name: 'studyCurrentSites', params: { studyId } });
      } else if (code === 5 || code === 6 || code === 16) {
        this.$router.push({ name: 'studyEditAddresses', params: { studyId } });
      } else if (code === 7) {
        this.$router.push({ name: 'studyEditDetails', params: { studyId } });
      } else if (code === 9) {
        this.$router.push({ name: 'studySummary', params: { studyId } });
      } else if (code === 10 || code === 11) {
        this.$router.push({ name: 'studyStatus', params: { studyId } });
      } else {
        this.$router.push({ name: 'studyUploadCSV', params: { studyId } });
      }
    },

    openEditName() {
      this.nameError = false;
      this.nameEdit = true;
      setTimeout(() => {
        this.$refs.name.focus();
      }, 100);
    },

    editName() {
      this.nameError = false;
      this.edited = true;
      const { id } = this.data;
      this.$store
        .dispatch('updateStudyName', { id, name: this.name })
        .then(() => {
          this.nameEdit = false;
          this.prevName = this.name;
        })
        .catch(() => {
          this.edited = false;
          this.nameError = true;
          this.nameEdit = true;
        });
    },

    cancelEditName() {
      setTimeout(() => {
        this.nameEdit = false;
        if (!this.edited) {
          this.name = this.prevName;
        }

        this.edited = false;
      }, 100);
    },

    uploadCSV() {
      const { id } = this.data;
      this.$router.push({ name: 'studyUploadCSV', params: { studyId: id } });
    },

    seeResult() {
      const { id } = this.data;
      this.fetching = true;
      this.$router.push({
        name: this.isDesktop ? 'synthesis' : 'result',
        params: { studyId: id },
        hash: this.isDesktop ? '' : '#synthesis',
      });
    },

    duplicateStudy() {
      const { id } = this.data;
      this.$store
        .dispatch('createStudyDuplicate', { studyId: id, studyName: this.name })
        .then(async res => {
          const processStatus = await this.$store.dispatch('fetchProcessStatus', {
            id: res.public_id,
          });

          if (processStatus.code > 11)
            this.$router.push({
              name: this.isDesktop ? 'synthesis' : 'result',
              params: { studyId: res.public_id },
              hash: this.isDesktop ? '' : '#synthesis',
            });
          else this.editStudy({ studyId: res.public_id, code: processStatus.code });
        })
        .catch(e => {
          if (e.status === 409) {
            alert(this.$t('studyAlreadyDuplicated'));
          } else {
            alert(this.$t('duplicationError'));
          }
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/styles/colors.scss';

.card {
  position: relative;
  display: flex;
  width: 100%;
  height: 126px;
  padding: 12px 0 0 0;
  box-sizing: border-box;
  border-bottom: 1px solid colors.$clear-border-color;
  transition: all 0.3s ease;

  &:hover {
    .info-container {
      .name {
        .name-edit {
          opacity: 1;
        }
      }
    }
  }

  .icon-holder {
    width: 64px;
    height: 64px;
    margin: 16px 32px auto 48px;
    border-radius: 4px;
    display: flex;
    justify-content: center;
    align-items: center;

    i {
      font-size: 80px;
      color: #dbd6c7;
    }
  }

  .info-container {
    text-align: left;

    .name {
      color: #3c3c3b;
      font-size: 22px;
      font-weight: 600;
      max-width: 544px;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow-x: hidden;

      input {
        width: calc(100% - 64px);

        &.error {
          border-color: colors.$red-color;
        }
      }

      .name-label {
        display: inline-block;
        max-width: 512px;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow-x: hidden;
      }

      .icon-edit-name {
        $size: 24px;
        opacity: 0;
        display: inline-block;
        width: $size;
        height: $size;
        margin: 0 0 2px 8px;
        transition: 0.2s;
      }

      .name-edit {
        cursor: pointer;

        &:hover {
          &.icon-edit-name {
            opacity: 0.4;
          }

          color: #bc141a;
          transform: rotate(-30deg) scaleX(-1);
        }
      }
    }

    .client {
      color: #9d9c9c;
      font-size: 18px;
    }
    .meta-container {
      display: flex;
      margin: 18px 0 0 0;

      .date {
        width: 220px;
      }
      .author {
        width: 256px;
        line-height: 1em;

        .country {
          opacity: 0.6;
          font-size: 12px;
          display: flex;
          padding-right: 16px;
          span {
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
          }
        }
      }
      .status {
        position: relative;
        display: flex;
        width: 160px;

        .text {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        &::before {
          content: '';
          position: absolute;
          left: -12px;
          top: 6px;
          width: 8px;
          height: 8px;
          border-radius: 8px;
          background: colors.$red-color;
        }

        &.valid {
          &::before {
            background: #34cc83;
          }
        }
      }
    }
  }

  .action-container {
    display: flex;
    align-items: flex-end;
    flex-direction: column;
    margin: 16px 0 0 auto;

    .icon-container {
      display: flex;
      justify-content: flex-end;

      .icon {
        width: 24px;
        height: 24px;
        margin: 0 6px 20px;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        position: relative;

        &.active {
          color: #bc141a;
        }

        i {
          font-size: 1.5em;

          &:hover {
            color: #bc141a;
          }
        }

        .icon-edit {
          $size: 24px;
          display: inline-block;
          width: $size;
          height: $size;

          &.active {
            background-image: url('../../assets/icon-edit-red.svg');
          }

          &:hover {
            background-image: url('../../assets/icon-edit-red.svg');
          }
        }

        .icon-download {
          margin-right: -10px;
        }

        .icon-duplicate {
          background-image: url('../../assets/duplicate.svg');
          width: 20px;
          height: 20px;

          &:hover {
            background-image: url('../../assets/duplicate-red.svg');
          }
        }

        .select-box {
          border: 1px solid #e3e3e3;
          position: absolute;
          bottom: 100%;
          background-color: white;
          left: 50%;
          transform: translateX(-50%);
          visibility: hidden;
          opacity: 0;
          transition: all 0.3s ease;
          box-shadow: 0 2px 2px rgba(200, 200, 200, 0.3);
          border-radius: 2px;

          .select-entry {
            font-size: 12px;
            font-weight: 600;
            white-space: nowrap;
            padding: 1em;

            & + .select-entry {
              border-top: 1px solid #e3e3e3;
            }
          }
        }

        &:hover {
          .select-box {
            visibility: visible;
            opacity: 1;
          }
        }
      }
    }
  }
}
</style>
